<template>
  <div class="authenticator-custom" v-if="auth.route !== 'verifyUser'">
    <Authenticator :formFields="formFields" :login-mechanisms="['email']">
      <template v-slot:sign-in-header>
        <div class="amplify-heading amplify-heading--3">
          {{ $t('amplify.Sign in to your account') }}
        </div>
      </template>
      <template v-slot:sign-in-footer>
        <span class="authenticator-custom__forgot-your-password">
          {{ $t('amplify.Forgot your password? ') }}
          <a
            data-test="sign-in-forgot-password-link"
            @click="toForgotPassword()"
          >
            {{ $t('amplify.Reset password') }}
          </a>
        </span>
        <span class="authenticator-custom__sign-up-in-link">
          {{ $t('amplify.No account? ') }}
          <a data-test="sign-in-create-account-link" @click="toSignUp()">
            {{ $t('amplify.Create account') }}
          </a>
        </span>
      </template>

      <template v-slot:sign-up-header>
        <div class="amplify-heading amplify-heading--3">
          {{ $t('amplify.Create a new account') }}
        </div>
      </template>
      <template v-slot:sign-up-footer>
        <span class="authenticator-custom__sign-up-in-link">
          {{ $t('amplify.Have an account? ') }}
          <a data-test="sign-in-link" @click="toSignIn()">
            {{ $t('amplify.Sign in') }}
          </a>
        </span>
      </template>

      <template v-slot:forgot-password-header>
        <div class="amplify-heading amplify-heading--3">
          {{ $t('amplify.Reset your password') }}
        </div>
      </template>

      <template v-slot:confirm-sign-in-header>
        <div class="amplify-heading amplify-heading--3">
          {{ $t('amplify.Confirm Sign In') }}
        </div>
        <p class="amplify-text authentication__mfa-code-message">
          {{ $t('amplify.Apply code from MFA app') }}
        </p>
      </template>

      <template v-slot:confirm-sign-in-footer>
        <OCheckbox
          :modelValue="rememberDevice"
          @update:modelValue="toggleRememberDevice"
          root-class="amplify-text authenticator-custom__checkbox"
          >{{ $t('amplify.Remember this device') }}</OCheckbox
        >
      </template>
    </Authenticator>

    <Transition name="captcha-overlay">
      <div v-if="showCaptchaOverlay" class="captcha-overlay">
        <div class="captcha-overlay__card">
          <div
            class="amplify-heading amplify-heading--3 captcha-overlay__title"
          >
            {{ $t('captcha.title') }}
          </div>
          <p class="captcha-overlay__subtitle">
            {{ $t('captcha.subtitle') }}
          </p>
          <div v-if="captchaLoading" class="captcha-overlay__spinner" />
          <div
            v-show="!captchaLoading"
            ref="captchaContainer"
            class="captcha-overlay__widget"
          />
          <p v-if="captchaFailed" class="captcha-overlay__error">
            {{ $t('captcha.error') }}
          </p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { useAuthenticator } from '@aws-amplify/ui-vue';
import { defineComponent, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import useWafCaptcha, {
  registerCaptchaRequiredCallback,
  unregisterCaptchaRequiredCallback,
  getCaptchaToken,
} from '@/composables/useWafCaptcha';

export default defineComponent({
  name: 'AuthenticatorCustom',
  data(): any {
    return {
      formFields: {
        signIn: {
          username: {
            order: 1,
            label: this.$t('amplify.Email address'),
            placeholder: this.$t('amplify.Enter your Email address'),
          },
          password: {
            order: 2,
          },
        },
        signUp: {
          email: {
            order: 1,
            label: this.$t('amplify.Email address'),
            placeholder: this.$t('amplify.Enter your Email address'),
          },
          password: {
            order: 2,
            placeholder: this.$t('amplify.Enter your password'),
          },
          confirm_password: {
            order: 3,
            label: this.$t('amplify.Confirm password'),
            placeholder: this.$t('amplify.Please confirm your password'),
          },
        },
      },
    };
  },
  computed: {
    rememberDevice(): boolean {
      return this.$store.state.auth.rememberDevice;
    },
  },
  methods: {
    toggleRememberDevice(): void {
      this.$store.commit('auth/setRememberDevice', !this.rememberDevice);
    },
    toSignIn(): void {
      const facade = useAuthenticator();
      facade.toSignIn();
    },
    toSignUp(): void {
      const facade = useAuthenticator();
      facade.toSignUp();
    },
    toForgotPassword(): void {
      const facade = useAuthenticator();
      facade.toForgotPassword();
    },
  },
  setup() {
    const auth = useAuthenticator();
    const { locale } = useI18n();
    const captchaContainer = ref<HTMLElement | null>(null);
    const {
      solved: captchaSolved,
      failed: captchaFailed,
      loading: captchaLoading,
    } = useWafCaptcha(captchaContainer, locale);

    const showCaptchaOverlay = ref(false);
    const captchaResolve = ref<((token: string) => void) | null>(null);

    function waitForCaptcha(): Promise<string> {
      const token = getCaptchaToken();
      if (captchaSolved.value && token) return Promise.resolve(token);
      showCaptchaOverlay.value = true;
      return new Promise<string>((resolve) => {
        if (captchaResolve.value) {
          // Chain onto existing pending resolve so both callers unblock on solve.
          const prev = captchaResolve.value;
          captchaResolve.value = (token: string) => {
            prev(token);
            resolve(token);
          };
        } else {
          captchaResolve.value = resolve;
        }
      });
    }

    registerCaptchaRequiredCallback(waitForCaptcha);
    onUnmounted(() => unregisterCaptchaRequiredCallback());

    watch(captchaSolved, (solved) => {
      if (solved && captchaResolve.value) {
        captchaResolve.value(getCaptchaToken() as string);
        captchaResolve.value = null;
        showCaptchaOverlay.value = false;
      }
    });

    return {
      auth,
      captchaContainer,
      captchaFailed,
      captchaLoading,
      showCaptchaOverlay,
    };
  },
});
</script>

<style lang="scss" scoped>
.authenticator-custom {
  position: relative;

  &__checkbox {
    color: $primary;
    font-size: 16px;
    order: 1;

    &.b-checkbox.checkbox :deep(.control-label) {
      padding: 2px 0 0 16px;
    }
  }

  :deep(.amplify-alert.amplify-alert--error) {
    order: 4;
    margin-bottom: 80px;
  }

  :deep(footer) {
    min-height: 110px;
    height: auto;
  }
}

// Overlay
.captcha-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 32, 64, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;

  &__card {
    background: white;
    border-radius: 8px;
    box-shadow: $card-box-shadow;
    padding: 35px 40px;
    max-width: 400px;
    width: 100%;
    margin: 0 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    margin-bottom: 8px;
    text-align: center;
  }

  &__subtitle {
    font-size: 14px;
    color: var(--amplify-colors-neutral-80);
    margin-bottom: 20px;
    text-align: center;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba($primary, 0.2);
    border-top-color: $primary;
    border-radius: 50%;
    animation: authenticator-captcha-spin 0.7s linear infinite;
    margin: 16px 0;
  }

  &__widget {
    width: 100%;
    min-height: 100px;
  }

  &__error {
    color: var(--amplify-colors-red-80);
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
  }
}

@keyframes authenticator-captcha-spin {
  to {
    transform: rotate(360deg);
  }
}

// Transition
.captcha-overlay-leave-active {
  transition: opacity 0.3s ease;

  .captcha-overlay__card {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
}

.captcha-overlay-leave-to {
  opacity: 0;

  .captcha-overlay__card {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.captcha-overlay-enter-active {
  transition: opacity 0.2s ease;
}

.captcha-overlay-enter-from {
  opacity: 0;
}
</style>
